import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useDesktopExpanded } from '../hooks/useDesktopExpanded';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import '../styles/desktop-expanded.css';

function Statistics() {
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [gradeTestData, setGradeTestData] = useState([]);
  const [schoolStudents, setSchoolStudents] = useState([]);
  const [subjectTestData, setSubjectTestData] = useState([]);
  const [teacherStudents, setTeacherStudents] = useState([]);
  const [teacherTestData, setTeacherTestData] = useState([]); // 강사별 테스트 현황 데이터
  const [gradeExamData, setGradeExamData] = useState([]); // 학년별 테스트/리포트 현황 데이터
  const [dashboardStats, setDashboardStats] = useState(null); // 새로운 대시보드 통계 데이터
  const [lastRefreshTime, setLastRefreshTime] = useState(null); // 마지막 데이터 갱신 시간
  const [showSchoolTable, setShowSchoolTable] = useState(false); // 학교별 학생 현황 토글
  const [showTeacherTable, setShowTeacherTable] = useState(false); // 강사별 학생 현황 토글
  const [showProblemTable, setShowProblemTable] = useState(false); // 과목별 문제 편성 현황 토글
  
  // 데스크톱 확장 훅 사용
  const {
    isMobileView
  } = useDesktopExpanded({
    expandConfig: {
      mode: 'grid',
      maintainMobileWidth: true
    }
  });

  useEffect(() => {
    const checkAuth = async () => {
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
      }
    };
    checkAuth();
  }, [getAccessToken, navigate]);

  // 대시보드 통계 데이터 가져오기
  const fetchDashboardStats = async () => {
    try {
        const token = await getAccessToken();
        const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/dashboard/stats`,
          {
            headers: {
              'Authorization': token,
              'Content-Type': 'application/json',
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
        console.log('대시보드 통계 데이터:', data);
        setDashboardStats(data.data);
        
        // timestamp를 UTC 시간으로 저장
        if (data.data && data.data.timestamp) {
          const timestamp = data.data.timestamp * 1000; // 초를 밀리초로 변환
          const date = new Date(timestamp);
          setLastRefreshTime(date);
        }
        
        // 새로운 API 데이터로 기존 차트 데이터도 업데이트
        if (data.data && data.data.student_fields) {
          updateChartsFromDashboardAPI(data.data);
        }
      }
    } catch (error) {
      console.error('대시보드 통계 조회 오류:', error);
    }
  };

  // 대시보드 API 데이터로 기존 차트 및 테이블 업데이트
  const updateChartsFromDashboardAPI = (apiData) => {
    const { student_fields, exam_fields, problem_fields } = apiData;
    
    // 학년별 학생 분포 차트 데이터 업데이트 (특별 강사 학생 제외)
    if (student_fields.students_by_grade) {
      // 특별 강사 목록
      const specialTeachers = ['데스크', 'unassigned', '테스트', '문제입력', '데스크2'];

      // 특별 강사의 학년별 합계 계산
      let specialByGrade = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0, 11: 0, 12: 0 };
      if (student_fields.students_by_teacher) {
        Object.entries(student_fields.students_by_teacher).forEach(([teacherName, grades]) => {
          if (specialTeachers.includes(teacherName)) {
            for (let i = 1; i <= 12; i++) {
              specialByGrade[i] += grades[i] || 0;
            }
          }
        });
      }

      const chartData = [];

      // 초등 (배열 인덱스 1~6 합계) - 특별 강사 학생 제외
      const elemTotal = (student_fields.students_by_grade[1] || 0) + (student_fields.students_by_grade[2] || 0) +
                        (student_fields.students_by_grade[3] || 0) + (student_fields.students_by_grade[4] || 0) +
                        (student_fields.students_by_grade[5] || 0) + (student_fields.students_by_grade[6] || 0);
      const specialElemTotal = specialByGrade[1] + specialByGrade[2] + specialByGrade[3] +
                               specialByGrade[4] + specialByGrade[5] + specialByGrade[6];
      chartData.push({ grade: '초등', count: elemTotal - specialElemTotal });

      // 중1~중3 (배열 인덱스 7,8,9) - 특별 강사 학생 제외
      chartData.push(
        { grade: '중1', count: (student_fields.students_by_grade[7] || 0) - specialByGrade[7] },
        { grade: '중2', count: (student_fields.students_by_grade[8] || 0) - specialByGrade[8] },
        { grade: '중3', count: (student_fields.students_by_grade[9] || 0) - specialByGrade[9] }
      );

      // 고1~고3 (배열 인덱스 10,11,12) - 특별 강사 학생 제외
      chartData.push(
        { grade: '고1', count: (student_fields.students_by_grade[10] || 0) - specialByGrade[10] },
        { grade: '고2', count: (student_fields.students_by_grade[11] || 0) - specialByGrade[11] },
        { grade: '고3', count: (student_fields.students_by_grade[12] || 0) - specialByGrade[12] }
      );

      setGradeTestData(chartData);
    }

    // 학교별 학생 현황 테이블 데이터 업데이트
    if (student_fields.students_by_school) {
      const schoolData = Object.entries(student_fields.students_by_school).map(([schoolName, grades]) => {
        // grades: [합계, 초1~초6, 중1~중3, 고1~고3]

        // 학교 타입 구분
        let schoolType = 'high'; // 기본값: 고등학교
        if (schoolName.includes('초') || schoolName.includes('초등')) {
          schoolType = 'elementary';
        } else if (schoolName.includes('중')) {
          schoolType = 'middle';
        }

        if (schoolType === 'elementary') {
          // 초등학교: 초1~초6 합계를 하나로 표시
          const elemTotal = (grades[1] || 0) + (grades[2] || 0) + (grades[3] || 0) +
                            (grades[4] || 0) + (grades[5] || 0) + (grades[6] || 0);
          return {
            schoolName,
            schoolType,
            grade1: elemTotal,  // 초등 전체
            grade2: 0,
            grade3: 0,
            total: grades[0] || 0
          };
        } else if (schoolType === 'middle') {
          return {
            schoolName,
            schoolType,
            grade1: grades[7] || 0,  // 중1
            grade2: grades[8] || 0,  // 중2
            grade3: grades[9] || 0,  // 중3
            total: grades[0] || 0
          };
        } else {
          return {
            schoolName,
            schoolType,
            grade1: grades[10] || 0,  // 고1
            grade2: grades[11] || 0,  // 고2
            grade3: grades[12] || 0,  // 고3
            total: grades[0] || 0
          };
        }
      }).filter(school => school.total > 0) // 학생이 있는 학교만
        .sort((a, b) => b.total - a.total); // 학생 수로 내림차순 정렬

      setSchoolStudents(schoolData);
    }

    // 강사별 학생 현황 테이블 데이터 업데이트
    if (student_fields.students_by_teacher) {
      // 특별 강사 목록 (가장 아래로 배치, 연한 회색 음영 적용)
      const specialTeachers = ['데스크', 'unassigned', '테스트', '문제입력', '데스크2'];

      const teacherData = Object.entries(student_fields.students_by_teacher).map(([teacherName, grades]) => {
        // 초등 합계 (초1~초6)
        const elemTotal = (grades[1] || 0) + (grades[2] || 0) + (grades[3] || 0) +
                          (grades[4] || 0) + (grades[5] || 0) + (grades[6] || 0);
        return {
          teacherName,
          '초등': elemTotal,
          '중1': grades[7] || 0,
          '중2': grades[8] || 0,
          '중3': grades[9] || 0,
          '고1': grades[10] || 0,
          '고2': grades[11] || 0,
          '고3': grades[12] || 0,
          total: grades[0] || 0,
          isSpecial: specialTeachers.includes(teacherName)
        };
      }).sort((a, b) => {
        // 특별 강사는 맨 아래로 배치
        if (a.isSpecial && !b.isSpecial) return 1;
        if (!a.isSpecial && b.isSpecial) return -1;
        // 둘 다 특별 강사이거나 둘 다 일반 강사면 학생 수로 내림차순 정렬
        return b.total - a.total;
      });

      setTeacherStudents(teacherData);
    }

    // 과목별 테스트/리포트 현황 차트 데이터 업데이트
    if (exam_fields && (exam_fields.exams_by_tree || exam_fields.reports_by_tree)) {
      // 모든 트리명 수집
      const allTrees = new Set();
      if (exam_fields.exams_by_tree) {
        Object.keys(exam_fields.exams_by_tree).forEach(tree => allTrees.add(tree));
      }
      if (exam_fields.reports_by_tree) {
        Object.keys(exam_fields.reports_by_tree).forEach(tree => allTrees.add(tree));
      }
      
      const chartData = Array.from(allTrees).map(subject => ({
        subject,
        testCount: exam_fields.exams_by_tree?.[subject] || 0,
        reportCount: exam_fields.reports_by_tree?.[subject] || 0
      })).sort((a, b) => (b.testCount + b.reportCount) - (a.testCount + a.reportCount));
      
      setSubjectTestData(chartData);
    }

    // 강사별 테스트 현황 차트 데이터 업데이트
    if (exam_fields && (exam_fields.exams_by_teacher || exam_fields.reports_by_teacher)) {
      // 모든 강사명 수집
      const allTeachers = new Set();
      if (exam_fields.exams_by_teacher) {
        Object.keys(exam_fields.exams_by_teacher).forEach(teacher => allTeachers.add(teacher));
      }
      if (exam_fields.reports_by_teacher) {
        Object.keys(exam_fields.reports_by_teacher).forEach(teacher => allTeachers.add(teacher));
      }
      
      const chartData = Array.from(allTeachers).map(teacher => ({
        teacher,
        testCount: exam_fields.exams_by_teacher?.[teacher] || 0,
        reportCount: exam_fields.reports_by_teacher?.[teacher] || 0
      })).sort((a, b) => (b.testCount + b.reportCount) - (a.testCount + a.reportCount))
        .slice(0, 8); // 최대 8개로 제한
      
      setTeacherTestData(chartData);
    }
    
    // 학년별 테스트/리포트 현황을 위한 추가 데이터 처리
    if (exam_fields && (exam_fields.exams_by_grade || exam_fields.reports_by_grade)) {
      // 학년 매핑: 7(중1), 8(중2), 9(중3), 10(고1), 11(고2), 12(고3)
      const gradeMapping = {
        7: '중1', 8: '중2', 9: '중3',
        10: '고1', 11: '고2', 12: '고3'
      };
      
      // 모든 학년 수집
      const allGrades = new Set();
      if (exam_fields.exams_by_grade) {
        Object.keys(exam_fields.exams_by_grade).forEach(grade => {
          const gradeNum = parseInt(grade);
          if (gradeMapping[gradeNum]) allGrades.add(gradeNum);
        });
      }
      if (exam_fields.reports_by_grade) {
        Object.keys(exam_fields.reports_by_grade).forEach(grade => {
          const gradeNum = parseInt(grade);
          if (gradeMapping[gradeNum]) allGrades.add(gradeNum);
        });
      }
      
      const chartData = Array.from(allGrades).map(gradeNum => {
        const testCount = exam_fields.exams_by_grade?.[gradeNum] || 0;
        const reportCount = exam_fields.reports_by_grade?.[gradeNum] || 0;
        
        return {
          grade: gradeMapping[gradeNum],
          testCount,
          reportCount
        };
      }).filter(item => item.testCount > 0 || item.reportCount > 0) // 둘 다 0이면 제외
        .sort((a, b) => {
          // 학년 순서로 정렬 (중1, 중2, 중3, 고1, 고2, 고3)
          const gradeOrder = ['중1', '중2', '중3', '고1', '고2', '고3'];
          return gradeOrder.indexOf(a.grade) - gradeOrder.indexOf(b.grade);
        });
      
      setGradeExamData(chartData);
    }


  };

  // timestamp를 UTC 시간 문자열로 변환
  const formatUTCTime = (date) => {
    if (!date) return '';
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds} KST`;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        
        // 대시보드 통계 데이터 가져오기 (모든 데이터가 여기서 처리됨)
        await fetchDashboardStats();
        
      } catch (error) {
        console.error('데이터 조회 오류:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [getAccessToken]);

  // 통계 카드 컴포넌트
  const StatisticCard = ({ title, value, subValue, className = '' }) => (
    <div className={`rounded-3xl bg-white p-4 shadow-md sm:p-6 cursor-pointer hover:bg-gray-50 ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <h4 className="mb-1 text-base font-bold text-gray-600 sm:mb-2 sm:text-lg">{title}</h4>
          <div className="flex flex-col text-right">
            <div>
              <p className={`flex items-center justify-end text-right text-4xl font-bold sm:text-5xl ${
                (value === 0 || value === '0') ? 'text-gray-400' : 'text-primary'
              }`}>
                {value}
        {subValue && (
                  <span className="text-sm text-gray-500 ml-2">{subValue}</span>
        )}
              </p>
      </div>
    </div>
        </div>
      </div>
    </div>
  );



  return (
    <Layout>
      <div className="h-full">
        {/* 데이터 갱신 시간 표시 */}
        {lastRefreshTime && (
          <div className="mb-4">
            <div className="text-xs text-gray-500 bg-gray-50 px-3 py-1 border text-center rounded-3xl">
              데이터 기준 시간: {formatUTCTime(lastRefreshTime)}
            </div>
          </div>
        )}
        
        <div className="space-y-4 pb-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="mb-4 text-lg text-gray-600">데이터를 불러오는 중...</div>
              </div>
            </div>
          ) : (
            <>


              {/* 주요 통계 카드 섹션 */}
              <div className={isMobileView ? "grid grid-cols-2 gap-3 sm:gap-4" : "grid grid-cols-2 gap-4 w-full"}>
                <StatisticCard
                  title="전체 학생 수"
                  value={(() => {
                    const specialTeacherNames = ['데스크', 'unassigned', '테스트', '문제입력', '데스크2'];
                    const allTeachers = dashboardStats?.student_fields?.students_by_teacher;
                    if (!allTeachers) return 0;
                    return Object.entries(allTeachers)
                      .filter(([name]) => !specialTeacherNames.includes(name))
                      .reduce((sum, [, grades]) => sum + (grades[0] || 0), 0);
                  })()}
                  subValue="명"
                />
                <StatisticCard
                  title="전체 강사 수"
                  value={(() => {
                    const specialTeacherNames = ['데스크', 'unassigned', '테스트', '문제입력', '데스크2'];
                    const allTeachers = dashboardStats?.student_fields?.students_by_teacher;
                    if (!allTeachers) return 0;
                    return Object.keys(allTeachers).filter(name => !specialTeacherNames.includes(name)).length;
                  })()}
                  subValue="명"
                />
              </div>

              {/* 차트 섹션 1 */}
              <div className="grid grid-cols-1 gap-4 w-full">
                <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-base font-semibold text-gray-700">학년별 학생 분포</h4>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-gray-600">
                        초등학생 <span className="font-semibold text-emerald-600">{gradeTestData.find(d => d.grade === '초등')?.count || 0}명</span>
                      </span>
                      <span className="text-gray-600">
                        중학생 <span className="font-semibold text-emerald-600">{gradeTestData.filter(d => d.grade.startsWith('중')).reduce((sum, d) => sum + d.count, 0)}명</span>
                      </span>
                      <span className="text-gray-600">
                        고등학생 <span className="font-semibold text-emerald-600">{gradeTestData.filter(d => d.grade.startsWith('고')).reduce((sum, d) => sum + d.count, 0)}명</span>
                      </span>
                      <span className="text-gray-600">
                        총 <span className="font-bold text-emerald-700">{gradeTestData.reduce((sum, d) => sum + d.count, 0)}명</span>
                      </span>
                    </div>
                  </div>
                  <div className="recharts-responsive-container" style={{ width: '100%', height: '100%', minWidth: 0 }}>
                    <div className="recharts-wrapper" style={{ position: 'relative', cursor: 'default', width: '100%', height: '100%', maxHeight: 128, maxWidth: '100%' }}>
                      <ResponsiveContainer width="100%" height={128}>
                        <BarChart
                          data={gradeTestData}
                          margin={{ top: 2, right: 5, left: -5, bottom: 2 }}
                          barGap={4}
                          barCategoryGap="20%"
                        >
                          <defs>
                            <linearGradient id="colorGrade" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#10B981" stopOpacity={0.7}/>
                              <stop offset="95%" stopColor="#059669" stopOpacity={0.7}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                          <XAxis 
                            dataKey="grade" 
                            tick={{ fontSize: 11, fill: '#9ca3af' }} 
                            stroke="#f3f4f6"
                            axisLine={{ stroke: '#f3f4f6' }}
                            tickLine={false}
                          />
                          <YAxis 
                            tick={{ fontSize: 11, fill: '#9ca3af' }} 
                            stroke="#f3f4f6"
                            axisLine={false}
                            tickLine={false}
                            width={30}
                          />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'rgba(255, 255, 255, 0.98)', 
                              border: '1px solid #e5e7eb', 
                              borderRadius: '8px',
                              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                              fontSize: '12px'
                            }}
                            labelStyle={{ color: '#374151', fontWeight: 600, fontSize: '12px' }}
                            cursor={{ fill: 'rgba(16, 185, 129, 0.05)' }}
                          />
                          <Bar
                            dataKey="count"
                            fill="url(#colorGrade)"
                            radius={[6, 6, 0, 0]}
                            maxBarSize={40}
                          >
                            <LabelList dataKey="count" position="top" style={{ fontSize: '11px', fill: '#059669', fontWeight: 600 }} />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>

              {/* 학교별 학생 현황 테이블 섹션 */}
              <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-5">
                <div
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setShowSchoolTable(!showSchoolTable)}
                >
                  <h4 className="text-base font-semibold text-gray-700">학교별 학생 현황</h4>
                  <svg
                    className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${showSchoolTable ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {showSchoolTable && (
                <div className="overflow-x-auto mt-3">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 whitespace-nowrap">학교명</th>
                        <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500 whitespace-nowrap">1학년</th>
                        <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500 whitespace-nowrap">2학년</th>
                        <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500 whitespace-nowrap">3학년</th>
                        <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500 whitespace-nowrap">전체</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {schoolStudents.length > 0 ? (
                        (() => {
                          const highSchools = schoolStudents.filter(s => s.schoolType === 'high');
                          const middleSchools = schoolStudents.filter(s => s.schoolType === 'middle');
                          const elemSchools = schoolStudents.filter(s => s.schoolType === 'elementary');

                          // 특별 강사 합계 계산
                          const specialTeachers = teacherStudents.filter(t => t.isSpecial);
                          const specialHigh = {
                            grade1: specialTeachers.reduce((sum, t) => sum + (t['고1'] || 0), 0),
                            grade2: specialTeachers.reduce((sum, t) => sum + (t['고2'] || 0), 0),
                            grade3: specialTeachers.reduce((sum, t) => sum + (t['고3'] || 0), 0),
                            total: specialTeachers.reduce((sum, t) => sum + (t['고1'] || 0) + (t['고2'] || 0) + (t['고3'] || 0), 0)
                          };
                          const specialMiddle = {
                            grade1: specialTeachers.reduce((sum, t) => sum + (t['중1'] || 0), 0),
                            grade2: specialTeachers.reduce((sum, t) => sum + (t['중2'] || 0), 0),
                            grade3: specialTeachers.reduce((sum, t) => sum + (t['중3'] || 0), 0),
                            total: specialTeachers.reduce((sum, t) => sum + (t['중1'] || 0) + (t['중2'] || 0) + (t['중3'] || 0), 0)
                          };
                          const specialElem = {
                            total: specialTeachers.reduce((sum, t) => sum + (t['초등'] || 0), 0)
                          };

                          return (
                            <>
                              {/* 고등학교 섹션 */}
                              {highSchools.length > 0 && (
                                <>
                                  {highSchools.map((school, index) => (
                                    <tr key={`high-${index}`}>
                                      <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">{school.schoolName}</td>
                                      <td className="px-4 py-3 text-sm text-gray-600 text-center">{school.grade1}</td>
                                      <td className="px-4 py-3 text-sm text-gray-600 text-center">{school.grade2}</td>
                                      <td className="px-4 py-3 text-sm text-gray-600 text-center">{school.grade3}</td>
                                      <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-center">{school.total}</td>
                                    </tr>
                                  ))}
                                  <tr className="bg-purple-50 font-semibold">
                                    <td className="px-4 py-3 text-sm font-bold text-purple-900 whitespace-nowrap">고등학교 소계</td>
                                    <td className="px-4 py-3 text-sm text-purple-700 text-center">
                                      {highSchools.reduce((sum, s) => sum + s.grade1, 0) - specialHigh.grade1}
                                      <span className="text-xs text-gray-400 ml-1">/ {specialHigh.grade1}</span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-purple-700 text-center">
                                      {highSchools.reduce((sum, s) => sum + s.grade2, 0) - specialHigh.grade2}
                                      <span className="text-xs text-gray-400 ml-1">/ {specialHigh.grade2}</span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-purple-700 text-center">
                                      {highSchools.reduce((sum, s) => sum + s.grade3, 0) - specialHigh.grade3}
                                      <span className="text-xs text-gray-400 ml-1">/ {specialHigh.grade3}</span>
                                    </td>
                                    <td className="px-4 py-3 text-sm font-bold text-purple-700 text-center">
                                      {highSchools.reduce((sum, s) => sum + s.total, 0) - specialHigh.total}
                                      <span className="text-xs text-gray-400 ml-1">/ {specialHigh.total}</span>
                                    </td>
                                  </tr>
                                </>
                              )}

                              {/* 중학교 섹션 */}
                              {middleSchools.length > 0 && (
                                <>
                                  {middleSchools.map((school, index) => (
                                    <tr key={`middle-${index}`}>
                                      <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">{school.schoolName}</td>
                                      <td className="px-4 py-3 text-sm text-gray-600 text-center">{school.grade1}</td>
                                      <td className="px-4 py-3 text-sm text-gray-600 text-center">{school.grade2}</td>
                                      <td className="px-4 py-3 text-sm text-gray-600 text-center">{school.grade3}</td>
                                      <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-center">{school.total}</td>
                                    </tr>
                                  ))}
                                  <tr className="bg-emerald-50 font-semibold">
                                    <td className="px-4 py-3 text-sm font-bold text-emerald-900 whitespace-nowrap">중학교 소계</td>
                                    <td className="px-4 py-3 text-sm text-emerald-700 text-center">
                                      {middleSchools.reduce((sum, s) => sum + s.grade1, 0) - specialMiddle.grade1}
                                      <span className="text-xs text-gray-400 ml-1">/ {specialMiddle.grade1}</span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-emerald-700 text-center">
                                      {middleSchools.reduce((sum, s) => sum + s.grade2, 0) - specialMiddle.grade2}
                                      <span className="text-xs text-gray-400 ml-1">/ {specialMiddle.grade2}</span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-emerald-700 text-center">
                                      {middleSchools.reduce((sum, s) => sum + s.grade3, 0) - specialMiddle.grade3}
                                      <span className="text-xs text-gray-400 ml-1">/ {specialMiddle.grade3}</span>
                                    </td>
                                    <td className="px-4 py-3 text-sm font-bold text-emerald-700 text-center">
                                      {middleSchools.reduce((sum, s) => sum + s.total, 0) - specialMiddle.total}
                                      <span className="text-xs text-gray-400 ml-1">/ {specialMiddle.total}</span>
                                    </td>
                                  </tr>
                                </>
                              )}

                              {/* 초등학교 섹션 */}
                              {elemSchools.length > 0 && (
                                <>
                                  {elemSchools.map((school, index) => (
                                    <tr key={`elem-${index}`}>
                                      <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">{school.schoolName}</td>
                                      <td className="px-4 py-3 text-sm text-gray-400 text-center">-</td>
                                      <td className="px-4 py-3 text-sm text-gray-400 text-center">-</td>
                                      <td className="px-4 py-3 text-sm text-gray-400 text-center">-</td>
                                      <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-center">{school.total}</td>
                                    </tr>
                                  ))}
                                  <tr className="bg-amber-50 font-semibold">
                                    <td className="px-4 py-3 text-sm font-bold text-amber-900 whitespace-nowrap">초등학교 소계</td>
                                    <td className="px-4 py-3 text-sm text-gray-400 text-center">-</td>
                                    <td className="px-4 py-3 text-sm text-gray-400 text-center">-</td>
                                    <td className="px-4 py-3 text-sm text-gray-400 text-center">-</td>
                                    <td className="px-4 py-3 text-sm font-bold text-amber-700 text-center">
                                      {elemSchools.reduce((sum, s) => sum + s.total, 0) - specialElem.total}
                                      <span className="text-xs text-gray-400 ml-1">/ {specialElem.total}</span>
                                    </td>
                                  </tr>
                                </>
                              )}

                              {/* 총합계 */}
                              <tr className="bg-blue-50 font-semibold">
                                <td className="px-4 py-3 text-sm font-bold text-gray-900 whitespace-nowrap">총합계</td>
                                <td className="px-4 py-3 text-sm text-blue-700 text-center">
                                  {schoolStudents.reduce((sum, s) => sum + s.grade1, 0) - specialHigh.grade1 - specialMiddle.grade1 - specialElem.total}
                                </td>
                                <td className="px-4 py-3 text-sm text-blue-700 text-center">
                                  {schoolStudents.reduce((sum, s) => sum + s.grade2, 0) - specialHigh.grade2 - specialMiddle.grade2}
                                </td>
                                <td className="px-4 py-3 text-sm text-blue-700 text-center">
                                  {schoolStudents.reduce((sum, s) => sum + s.grade3, 0) - specialHigh.grade3 - specialMiddle.grade3}
                                </td>
                                <td className="px-4 py-3 text-sm font-bold text-blue-700 text-center">
                                  {schoolStudents.reduce((sum, s) => sum + s.total, 0) - specialHigh.total - specialMiddle.total - specialElem.total}
                                  <span className="text-xs text-gray-400 ml-1">/ {specialHigh.total + specialMiddle.total + specialElem.total}</span>
                                </td>
                              </tr>
                            </>
                          );
                        })()
                      ) : (
                        <tr>
                          <td colSpan="5" className="px-4 py-3 text-sm text-gray-500 text-center">데이터를 불러오는 중...</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                )}
              </div>

              {/* 강사별 학생 현황 테이블 섹션 */}
              <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-5">
                <div
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setShowTeacherTable(!showTeacherTable)}
                >
                  <h4 className="text-base font-semibold text-gray-700">강사별 학생 현황</h4>
                  <svg
                    className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${showTeacherTable ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {showTeacherTable && (
                <div className="overflow-x-auto mt-3">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 whitespace-nowrap">강사명</th>
                        <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500 whitespace-nowrap">초등</th>
                        <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500 whitespace-nowrap">중1</th>
                        <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500 whitespace-nowrap">중2</th>
                        <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500 whitespace-nowrap">중3</th>
                        <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500 whitespace-nowrap">고1</th>
                        <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500 whitespace-nowrap">고2</th>
                        <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500 whitespace-nowrap">고3</th>
                        <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500 whitespace-nowrap">전체</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {teacherStudents.length > 0 ? (
                        <>
                          {/* 일반 강사 행들 (고3, 고2, 고1, 중3, 중2, 중1 순) */}
                          {teacherStudents.filter(t => !t.isSpecial).sort((a, b) => {
                            // 고3 → 고2 → 고1 → 중3 → 중2 → 중1 순으로 정렬
                            if (b['고3'] !== a['고3']) return b['고3'] - a['고3'];
                            if (b['고2'] !== a['고2']) return b['고2'] - a['고2'];
                            if (b['고1'] !== a['고1']) return b['고1'] - a['고1'];
                            if (b['중3'] !== a['중3']) return b['중3'] - a['중3'];
                            if (b['중2'] !== a['중2']) return b['중2'] - a['중2'];
                            return b['중1'] - a['중1'];
                          }).map((teacher, index) => (
                            <tr key={`normal-${index}`}>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">{teacher.teacherName}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 text-center">{teacher['초등'] || 0}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 text-center">{teacher['중1'] || 0}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 text-center">{teacher['중2'] || 0}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 text-center">{teacher['중3'] || 0}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 text-center">{teacher['고1'] || 0}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 text-center">{teacher['고2'] || 0}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 text-center">{teacher['고3'] || 0}</td>
                              <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-center">{teacher.total || 0}</td>
                            </tr>
                          ))}
                          {/* 합계 행 (일반 강사만) */}
                          <tr className="bg-blue-50 font-semibold">
                            <td className="px-4 py-3 text-sm font-bold text-gray-900 whitespace-nowrap">합계</td>
                            <td className="px-4 py-3 text-sm text-blue-700 text-center">
                              {teacherStudents.filter(t => !t.isSpecial).reduce((sum, teacher) => sum + (teacher['초등'] || 0), 0)}
                            </td>
                            <td className="px-4 py-3 text-sm text-blue-700 text-center">
                              {teacherStudents.filter(t => !t.isSpecial).reduce((sum, teacher) => sum + (teacher['중1'] || 0), 0)}
                            </td>
                            <td className="px-4 py-3 text-sm text-blue-700 text-center">
                              {teacherStudents.filter(t => !t.isSpecial).reduce((sum, teacher) => sum + (teacher['중2'] || 0), 0)}
                            </td>
                            <td className="px-4 py-3 text-sm text-blue-700 text-center">
                              {teacherStudents.filter(t => !t.isSpecial).reduce((sum, teacher) => sum + (teacher['중3'] || 0), 0)}
                            </td>
                            <td className="px-4 py-3 text-sm text-blue-700 text-center">
                              {teacherStudents.filter(t => !t.isSpecial).reduce((sum, teacher) => sum + (teacher['고1'] || 0), 0)}
                            </td>
                            <td className="px-4 py-3 text-sm text-blue-700 text-center">
                              {teacherStudents.filter(t => !t.isSpecial).reduce((sum, teacher) => sum + (teacher['고2'] || 0), 0)}
                            </td>
                            <td className="px-4 py-3 text-sm text-blue-700 text-center">
                              {teacherStudents.filter(t => !t.isSpecial).reduce((sum, teacher) => sum + (teacher['고3'] || 0), 0)}
                            </td>
                            <td className="px-4 py-3 text-sm font-bold text-blue-700 text-center">
                              {teacherStudents.filter(t => !t.isSpecial).reduce((sum, teacher) => sum + (teacher.total || 0), 0)}
                            </td>
                          </tr>
                          {/* 특별 강사 행들 (회색 음영, 강사명 오름차순) */}
                          {teacherStudents.filter(t => t.isSpecial).sort((a, b) => {
                            const nameA = a.teacherName === 'unassigned' ? '미배정' : a.teacherName;
                            const nameB = b.teacherName === 'unassigned' ? '미배정' : b.teacherName;
                            return nameA.localeCompare(nameB, 'ko');
                          }).map((teacher, index) => (
                            <tr key={`special-${index}`} className="bg-gray-100">
                              <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                                {teacher.teacherName === 'unassigned' ? '미배정' : teacher.teacherName}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600 text-center">{teacher['초등'] || 0}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 text-center">{teacher['중1'] || 0}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 text-center">{teacher['중2'] || 0}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 text-center">{teacher['중3'] || 0}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 text-center">{teacher['고1'] || 0}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 text-center">{teacher['고2'] || 0}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 text-center">{teacher['고3'] || 0}</td>
                              <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-center">{teacher.total || 0}</td>
                            </tr>
                          ))}
                          {/* 총합계 행 (모든 강사) */}
                          <tr className="bg-blue-50 font-semibold">
                            <td className="px-4 py-3 text-sm font-bold text-gray-900 whitespace-nowrap">총합계</td>
                            <td className="px-4 py-3 text-sm text-blue-700 text-center">
                              {teacherStudents.reduce((sum, teacher) => sum + (teacher['초등'] || 0), 0)}
                            </td>
                            <td className="px-4 py-3 text-sm text-blue-700 text-center">
                              {teacherStudents.reduce((sum, teacher) => sum + (teacher['중1'] || 0), 0)}
                            </td>
                            <td className="px-4 py-3 text-sm text-blue-700 text-center">
                              {teacherStudents.reduce((sum, teacher) => sum + (teacher['중2'] || 0), 0)}
                            </td>
                            <td className="px-4 py-3 text-sm text-blue-700 text-center">
                              {teacherStudents.reduce((sum, teacher) => sum + (teacher['중3'] || 0), 0)}
                            </td>
                            <td className="px-4 py-3 text-sm text-blue-700 text-center">
                              {teacherStudents.reduce((sum, teacher) => sum + (teacher['고1'] || 0), 0)}
                            </td>
                            <td className="px-4 py-3 text-sm text-blue-700 text-center">
                              {teacherStudents.reduce((sum, teacher) => sum + (teacher['고2'] || 0), 0)}
                            </td>
                            <td className="px-4 py-3 text-sm text-blue-700 text-center">
                              {teacherStudents.reduce((sum, teacher) => sum + (teacher['고3'] || 0), 0)}
                            </td>
                            <td className="px-4 py-3 text-sm font-bold text-blue-700 text-center">
                              {teacherStudents.reduce((sum, teacher) => sum + (teacher.total || 0), 0)}
                            </td>
                          </tr>
                        </>
                      ) : (
                        <tr>
                          <td colSpan="9" className="px-4 py-3 text-sm text-gray-500 text-center">데이터를 불러오는 중...</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                )}
              </div>

              {/* 추가 통계 카드 - 강사별 학생 현황 아래 */}
              <div className={isMobileView ? "grid grid-cols-2 gap-3 sm:gap-4" : "grid grid-cols-2 gap-4 w-full"}>
                <StatisticCard
                  title="전체 테스트(누적)"
                  value={dashboardStats?.exam_fields?.total_exams || 0}
                  subValue="건"
                />
                <StatisticCard
                  title="전체 리포트(누적)"
                  value={dashboardStats?.exam_fields?.total_reports || 0}
                  subValue="건"
                />
              </div>

              {/* 과목별 테스트 현황 - 강사별 학생 현황 다음에 별도 행으로 배치 */}
              <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-5">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-base font-semibold text-gray-700">과목별 테스트/리포트 현황(최근 30일)</h4>
                  <div className="flex items-center gap-4 text-xs">
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-3 h-3 rounded" style={{ background: 'linear-gradient(180deg, rgba(16, 185, 129, 0.7) 5%, rgba(5, 150, 105, 0.7) 95%)' }}></span>
                      <span className="text-gray-600">테스트</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-3 h-3 rounded" style={{ background: 'linear-gradient(180deg, rgba(59, 130, 246, 0.7) 5%, rgba(29, 78, 216, 0.7) 95%)' }}></span>
                      <span className="text-gray-600">리포트</span>
                    </span>
                  </div>
                </div>
                <div className="recharts-responsive-container" style={{ width: '100%', height: '100%', minWidth: 0 }}>
                  <div className="recharts-wrapper" style={{ position: 'relative', cursor: 'default', width: '100%', height: '100%', maxHeight: 128, maxWidth: '100%' }}>
                    <ResponsiveContainer width="100%" height={128}>
                      <BarChart
                        data={subjectTestData}
                        margin={{ top: 5, right: 5, left: -5, bottom: 30 }}
                        barGap={4}
                        barCategoryGap="20%"
                      >
                        <defs>
                          <linearGradient id="colorSubject" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10B981" stopOpacity={0.7}/>
                            <stop offset="95%" stopColor="#059669" stopOpacity={0.7}/>
                          </linearGradient>
                          <linearGradient id="colorSubjectReport" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.7}/>
                            <stop offset="95%" stopColor="#1D4ED8" stopOpacity={0.7}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                        <XAxis
                          dataKey="subject"
                          tick={{ fontSize: 10, fill: '#9ca3af' }}
                          stroke="#f3f4f6"
                          axisLine={{ stroke: '#f3f4f6' }}
                          tickLine={false}
                          angle={-45}
                          textAnchor="end"
                          interval={0}
                        />
                        <YAxis
                          tick={{ fontSize: 11, fill: '#9ca3af' }}
                          stroke="#f3f4f6"
                          axisLine={false}
                          tickLine={false}
                          width={30}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'rgba(255, 255, 255, 0.98)',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                            fontSize: '12px'
                          }}
                          labelStyle={{ color: '#374151', fontWeight: 600, fontSize: '12px' }}
                          cursor={{ fill: 'rgba(16, 185, 129, 0.05)' }}
                        />
                        <Bar
                          dataKey="testCount"
                          fill="url(#colorSubject)"
                          radius={[6, 6, 0, 0]}
                          maxBarSize={40}
                          name="테스트"
                        >
                          <LabelList dataKey="testCount" position="top" style={{ fontSize: 10, fill: '#059669' }} />
                        </Bar>
                        <Bar
                          dataKey="reportCount"
                          fill="url(#colorSubjectReport)"
                          radius={[6, 6, 0, 0]}
                          maxBarSize={40}
                          name="리포트"
                        >
                          <LabelList dataKey="reportCount" position="top" style={{ fontSize: 10, fill: '#1D4ED8' }} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* 학년별 테스트 현황 - 과목별 테스트 현황 아래에 복사 배치 */}
              <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-5">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-base font-semibold text-gray-700">학년별 테스트/리포트 현황(최근 30일)</h4>
                  <div className="flex items-center gap-4 text-xs">
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-3 h-3 rounded" style={{ background: 'linear-gradient(180deg, rgba(139, 92, 246, 0.7) 5%, rgba(124, 58, 237, 0.7) 95%)' }}></span>
                      <span className="text-gray-600">테스트</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-3 h-3 rounded" style={{ background: 'linear-gradient(180deg, rgba(239, 68, 68, 0.7) 5%, rgba(220, 38, 38, 0.7) 95%)' }}></span>
                      <span className="text-gray-600">리포트</span>
                    </span>
                  </div>
                </div>
                <div className="recharts-responsive-container" style={{ width: '100%', height: '100%', minWidth: 0 }}>
                  <div className="recharts-wrapper" style={{ position: 'relative', cursor: 'default', width: '100%', height: '100%', maxHeight: 128, maxWidth: '100%' }}>
                    <ResponsiveContainer width="100%" height={128}>
                      <BarChart
                        data={gradeExamData}
                        margin={{ top: 5, right: 5, left: -5, bottom: 0 }}
                        barGap={4}
                        barCategoryGap="20%"
                      >
                        <defs>
                          <linearGradient id="colorGradeTest" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.7}/>
                            <stop offset="95%" stopColor="#7C3AED" stopOpacity={0.7}/>
                          </linearGradient>
                          <linearGradient id="colorGradeReport" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#EF4444" stopOpacity={0.7}/>
                            <stop offset="95%" stopColor="#DC2626" stopOpacity={0.7}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                        <XAxis
                          dataKey="grade"
                          tick={{ fontSize: 11, fill: '#9ca3af' }}
                          stroke="#f3f4f6"
                          axisLine={{ stroke: '#f3f4f6' }}
                          tickLine={false}
                          angle={0}
                          textAnchor="middle"
                          interval={0}
                        />
                        <YAxis
                          tick={{ fontSize: 11, fill: '#9ca3af' }}
                          stroke="#f3f4f6"
                          axisLine={false}
                          tickLine={false}
                          width={30}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'rgba(255, 255, 255, 0.98)',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                            fontSize: '12px'
                          }}
                          labelStyle={{ color: '#374151', fontWeight: 600, fontSize: '12px' }}
                          cursor={{ fill: 'rgba(139, 92, 246, 0.05)' }}
                        />
                        <Bar
                          dataKey="testCount"
                          fill="url(#colorGradeTest)"
                          radius={[6, 6, 0, 0]}
                          maxBarSize={40}
                          name="테스트"
                        >
                          <LabelList dataKey="testCount" position="top" style={{ fontSize: 10, fill: '#7C3AED' }} />
                        </Bar>
                        <Bar
                          dataKey="reportCount"
                          fill="url(#colorGradeReport)"
                          radius={[6, 6, 0, 0]}
                          maxBarSize={40}
                          name="리포트"
                        >
                          <LabelList dataKey="reportCount" position="top" style={{ fontSize: 10, fill: '#DC2626' }} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* 강사별 테스트 현황 - 학년별 테스트 현황 다음에 배치 */}
              <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-5">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-base font-semibold text-gray-700">강사별 테스트/리포트 현황(최근 30일)</h4>
                  <div className="flex items-center gap-4 text-xs">
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-3 h-3 rounded" style={{ background: 'linear-gradient(180deg, rgba(245, 158, 11, 0.7) 5%, rgba(217, 119, 6, 0.7) 95%)' }}></span>
                      <span className="text-gray-600">테스트</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-3 h-3 rounded" style={{ background: 'linear-gradient(180deg, rgba(16, 185, 129, 0.7) 5%, rgba(5, 150, 105, 0.7) 95%)' }}></span>
                      <span className="text-gray-600">리포트</span>
                    </span>
                  </div>
                </div>
                <div className="recharts-responsive-container" style={{ width: '100%', height: '100%', minWidth: 0 }}>
                  <div className="recharts-wrapper" style={{ position: 'relative', cursor: 'default', width: '100%', height: '100%', maxHeight: 128, maxWidth: '100%' }}>
                    <ResponsiveContainer width="100%" height={128}>
                      <BarChart
                        data={teacherTestData}
                        margin={{ top: 5, right: 5, left: -5, bottom: 0 }}
                        barGap={4}
                        barCategoryGap="20%"
                      >
                        <defs>
                          <linearGradient id="colorTeacher" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.7}/>
                            <stop offset="95%" stopColor="#D97706" stopOpacity={0.7}/>
                          </linearGradient>
                          <linearGradient id="colorTeacherReport" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10B981" stopOpacity={0.7}/>
                            <stop offset="95%" stopColor="#059669" stopOpacity={0.7}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                        <XAxis
                          dataKey="teacher"
                          tick={{ fontSize: 10, fill: '#9ca3af' }}
                          stroke="#f3f4f6"
                          axisLine={{ stroke: '#f3f4f6' }}
                          tickLine={false}
                          angle={-45}
                          textAnchor="end"
                          interval={0}
                        />
                        <YAxis
                          tick={{ fontSize: 11, fill: '#9ca3af' }}
                          stroke="#f3f4f6"
                          axisLine={false}
                          tickLine={false}
                          width={30}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'rgba(255, 255, 255, 0.98)',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                            fontSize: '12px'
                          }}
                          labelStyle={{ color: '#374151', fontWeight: 600, fontSize: '12px' }}
                          cursor={{ fill: 'rgba(245, 158, 11, 0.05)' }}
                        />
                        <Bar
                          dataKey="testCount"
                          fill="url(#colorTeacher)"
                          radius={[6, 6, 0, 0]}
                          maxBarSize={40}
                          name="테스트"
                        >
                          <LabelList dataKey="testCount" position="top" style={{ fontSize: 10, fill: '#D97706' }} />
                        </Bar>
                        <Bar
                          dataKey="reportCount"
                          fill="url(#colorTeacherReport)"
                          radius={[6, 6, 0, 0]}
                          maxBarSize={40}
                          name="리포트"
                        >
                          <LabelList dataKey="reportCount" position="top" style={{ fontSize: 10, fill: '#059669' }} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* 추가 통계 카드 - 문제 현황 위에 배치 */}
              <div className={isMobileView ? "grid grid-cols-2 gap-3 sm:gap-4" : "grid grid-cols-2 gap-4 w-full"}>
                <StatisticCard
                  title="전체 문제 수"
                  value={dashboardStats?.problem_fields?.total_problems || 0}
                  subValue="개"
                />
                <StatisticCard
                  title="편성 문제 수"
                  value={dashboardStats?.problem_fields?.assigned_problems || 0}
                  subValue="개"
                />
              </div>

              {/* 과목별 문제 편성 현황 */}
              <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-5">
                <div
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setShowProblemTable(!showProblemTable)}
                >
                  <h4 className="text-base font-semibold text-gray-700">과목별 문제 편성 현황</h4>
                  <svg
                    className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${showProblemTable ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {showProblemTable && (
                <div className="overflow-x-auto mt-3">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 whitespace-nowrap">과목/트리</th>
                        <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500 whitespace-nowrap">Lv 1</th>
                        <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500 whitespace-nowrap">Lv 2</th>
                        <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500 whitespace-nowrap">Lv 3</th>
                        <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500 whitespace-nowrap">Lv 4</th>
                        <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500 whitespace-nowrap">Lv 5</th>
                        <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500 whitespace-nowrap">합계</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {dashboardStats?.problem_fields?.problems_by_tree ? (
                        Object.entries(dashboardStats.problem_fields.problems_by_tree)
                          .sort((a, b) => (b[1]?.[0] || 0) - (a[1]?.[0] || 0)) // 배열[0] 합계값으로 내림차순 정렬
                          .map(([treeName, levelCounts], index) => {
                            // levelCounts: [합계, Lv1, Lv2, Lv3, Lv4, Lv5]
                            const counts = Array.isArray(levelCounts) ? levelCounts : [0, 0, 0, 0, 0, 0];
                            return (
                              <tr key={index}>
                                <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">{treeName}</td>
                                <td className="px-4 py-3 text-sm text-gray-600 text-center">{(counts[1] || 0).toLocaleString()}</td>
                                <td className="px-4 py-3 text-sm text-gray-600 text-center">{(counts[2] || 0).toLocaleString()}</td>
                                <td className="px-4 py-3 text-sm text-gray-600 text-center">{(counts[3] || 0).toLocaleString()}</td>
                                <td className="px-4 py-3 text-sm text-gray-600 text-center">{(counts[4] || 0).toLocaleString()}</td>
                                <td className="px-4 py-3 text-sm text-gray-600 text-center">{(counts[5] || 0).toLocaleString()}</td>
                                <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-center">{(counts[0] || 0).toLocaleString()}</td>
                              </tr>
                            );
                          })
                      ) : (
                        <tr>
                          <td colSpan="7" className="px-4 py-3 text-sm text-gray-500 text-center">데이터를 불러오는 중...</td>
                        </tr>
                      )}
                      {/* 합계 행 */}
                      {dashboardStats?.problem_fields?.problems_by_tree && (
                        <tr className="bg-blue-50 font-semibold">
                          <td className="px-4 py-3 text-sm font-bold text-gray-900 whitespace-nowrap">합계</td>
                          <td className="px-4 py-3 text-sm font-bold text-blue-700 text-center">
                            {Object.values(dashboardStats.problem_fields.problems_by_tree)
                              .reduce((sum, levelCounts) => {
                                const counts = Array.isArray(levelCounts) ? levelCounts : [0, 0];
                                return sum + (counts[1] || 0);
                              }, 0)
                              .toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-sm font-bold text-blue-700 text-center">
                            {Object.values(dashboardStats.problem_fields.problems_by_tree)
                              .reduce((sum, levelCounts) => {
                                const counts = Array.isArray(levelCounts) ? levelCounts : [0, 0, 0];
                                return sum + (counts[2] || 0);
                              }, 0)
                              .toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-sm font-bold text-blue-700 text-center">
                            {Object.values(dashboardStats.problem_fields.problems_by_tree)
                              .reduce((sum, levelCounts) => {
                                const counts = Array.isArray(levelCounts) ? levelCounts : [0, 0, 0, 0];
                                return sum + (counts[3] || 0);
                              }, 0)
                              .toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-sm font-bold text-blue-700 text-center">
                            {Object.values(dashboardStats.problem_fields.problems_by_tree)
                              .reduce((sum, levelCounts) => {
                                const counts = Array.isArray(levelCounts) ? levelCounts : [0, 0, 0, 0, 0];
                                return sum + (counts[4] || 0);
                              }, 0)
                              .toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-sm font-bold text-blue-700 text-center">
                            {Object.values(dashboardStats.problem_fields.problems_by_tree)
                              .reduce((sum, levelCounts) => {
                                const counts = Array.isArray(levelCounts) ? levelCounts : [0, 0, 0, 0, 0, 0];
                                return sum + (counts[5] || 0);
                              }, 0)
                              .toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-sm font-bold text-blue-700 text-center">
                            {Object.values(dashboardStats.problem_fields.problems_by_tree)
                              .reduce((sum, levelCounts) => {
                                const counts = Array.isArray(levelCounts) ? levelCounts : [0];
                                return sum + (counts[0] || 0);
                              }, 0)
                              .toLocaleString()}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                )}
              </div>

              {/* 모바일 하단 네비게이션 여백 */}
              {isMobileView && (
                <div
                  style={{ height: 'calc(68px + env(safe-area-inset-bottom, 0px))' }}
                  aria-hidden="true"
                />
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default Statistics;